import { useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Building2,
  Compass,
  FileText,
  LayoutDashboard,
  Menu,
  MessagesSquare,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  ShoppingCart,
} from 'lucide-react'
import { RETURN_TO_KEY, useSessionStore } from '@/stores/session'
import { clearUser } from '@/lib/observability'
import { useMediaQuery } from '@/lib/use-media-query'
import { cn } from '@/lib/utils'
import { Dialog } from '@/components/ui/dialog'
import { Wordmark } from '@/components/wordmark'

const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/orders', label: 'Orders', icon: Package },
  { to: '/purchasing', label: 'Purchasing', icon: ShoppingCart },
  { to: '/requests', label: 'Requests', icon: MessagesSquare },
  { to: '/quotes', label: 'Quotes', icon: FileText },
  { to: '/invoices', label: 'Invoices', icon: Receipt },
  { to: '/account', label: 'Account', icon: Building2 },
  { to: '/getting-started', label: 'Getting started', icon: Compass },
] as const

const NAV_COLLAPSED_KEY = 'gl:hub:nav-collapsed'

function readStoredCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Signed-in chrome, Ledger layout: collapsible left sidebar (232→64px,
 * localStorage-persisted) carrying the wordmark and the section nav, a top bar
 * with the account context and sign-out, and a scrolling main column. Below
 * 1024px the sidebar is replaced by a drawer (`<dialog>`) behind the menu
 * button — the single `nav[aria-label="Sections"]` landmark lives in whichever
 * branch is rendered. The wide (non-narrow) branch is the default, so jsdom
 * (no matchMedia) exercises the sidebar.
 */
export function AppShell() {
  const contact = useSessionStore((s) => s.contact)
  const logout = useSessionStore((s) => s.logout)
  const navigate = useNavigate()
  const narrow = useMediaQuery('(max-width: 1023px)')
  const [collapsed, setCollapsed] = useState(readStoredCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Who's actually signed in: on an impersonation session that's the staff
  // actor (the contact is who they're acting *as*), otherwise the contact.
  const displayName = contact
    ? contact.impersonated
      ? (contact.actorName ?? 'Green Line staff')
      : `${contact.firstName} ${contact.lastName}`
    : null

  function toggleCollapsed() {
    const next = !collapsed
    setCollapsed(next)
    try {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0')
    } catch {
      // non-fatal
    }
  }

  function signOut() {
    try {
      window.localStorage.removeItem(RETURN_TO_KEY)
    } catch {
      // non-fatal
    }
    logout()
    clearUser()
    navigate('/login')
  }

  return (
    <div className="flex h-dvh bg-snow">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-(--z-overlay) focus:rounded-sm focus:border focus:border-rule focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:text-ink"
      >
        Skip to content
      </a>

      {!narrow ? (
        <aside
          className={cn(
            'flex shrink-0 flex-col border-r border-rule/60 bg-card',
            collapsed ? 'w-16' : 'w-[232px]',
          )}
        >
          <Link
            to="/"
            aria-label="Green Line Hub — dashboard"
            className={cn(
              'flex h-14 shrink-0 items-center border-b border-rule/60 no-underline',
              collapsed ? 'justify-center' : 'px-4',
            )}
          >
            {collapsed ? (
              <span className="manifest-rail font-mono text-sm font-bold tracking-wordmark text-brandDeep">
                GL
              </span>
            ) : (
              <Wordmark />
            )}
          </Link>

          <SectionsNav collapsed={collapsed} />

          <div className="shrink-0 border-t border-rule/60 p-2">
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-sm py-2 text-sm text-inkMid transition-colors duration-[180ms] ease-editorial hover:bg-cream hover:text-ink',
                collapsed ? 'justify-center' : 'px-3',
              )}
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" className="size-4 shrink-0" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="size-4 shrink-0" />
              )}
              {!collapsed ? <span>Collapse</span> : null}
            </button>
            {!collapsed ? (
              <a
                href="mailto:orders@greenlineingredients.com"
                className="micro-label mt-1 block truncate rounded-sm px-3 pb-1 transition-colors duration-[180ms] ease-editorial hover:text-brand"
              >
                orders@greenlineingredients.com
              </a>
            ) : null}
          </div>
        </aside>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-rule/60 bg-card px-6">
          <div className="flex min-w-0 items-center gap-4">
            {narrow ? (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-expanded={drawerOpen}
                aria-label="Open navigation"
                className="rounded-sm p-1 text-inkMid transition-colors duration-[180ms] ease-editorial hover:bg-cream hover:text-ink"
              >
                <Menu aria-hidden="true" className="size-5" />
              </button>
            ) : null}
            {contact ? (
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-sm font-medium text-ink">{contact.accountName}</span>
                <span className="micro-label truncate">{displayName}</span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={signOut}
            className="rounded-sm text-sm text-inkMid underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
          >
            Sign out
          </button>
        </header>

        {contact?.impersonated ? (
          // Brand, not amber — amber is reserved for needs-your-attention. This
          // is the "you are in a powerful mode" stripe: writes work and are
          // attributed to the actor.
          <div
            role="status"
            className="manifest-rail shrink-0 border-b border-rule/60 bg-brandTint/50"
          >
            <p className="py-2 pl-2 pr-6 font-mono text-[0.6875rem] uppercase tracking-eyebrow text-brandDeep">
              Acting as {contact.firstName} {contact.lastName} ({contact.accountName})
            </p>
          </div>
        ) : null}

        <main id="main" className="flex-1 overflow-y-auto px-6 py-8 lg:px-8">
          <Outlet />
        </main>
      </div>

      {narrow ? (
        <Dialog
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          labelledBy="hub-drawer-heading"
          className="m-0 h-full max-h-none w-72 rounded-none border-y-0 border-l-0 border-rule/60 p-0"
        >
          <div className="flex h-full flex-col">
            <h2 id="hub-drawer-heading" className="sr-only">
              Navigation
            </h2>
            <Link
              to="/"
              onClick={() => setDrawerOpen(false)}
              aria-label="Green Line Hub — dashboard"
              className="flex h-14 shrink-0 items-center border-b border-rule/60 px-4 no-underline"
            >
              <Wordmark />
            </Link>
            <SectionsNav onNavigate={() => setDrawerOpen(false)} />
            <a
              href="mailto:orders@greenlineingredients.com"
              className="micro-label block shrink-0 truncate border-t border-rule/60 px-5 py-4 transition-colors duration-[180ms] ease-editorial hover:text-brand"
            >
              orders@greenlineingredients.com
            </a>
          </div>
        </Dialog>
      ) : null}
    </div>
  )
}

/**
 * The single Sections landmark — rendered in the sidebar OR the mobile drawer,
 * never both. Active item wears the manifest rail (border-l-2 pine, an allowed
 * rail site); inactive items keep a transparent rail for alignment.
 */
function SectionsNav({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean
  onNavigate?: () => void
}) {
  return (
    <nav aria-label="Sections" className="flex-1 overflow-y-auto px-2 py-3">
      <ul className="space-y-1">
        {NAV_ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              // Without `end`, the "/" dashboard item would read active on
              // every route (react-router prefix matching).
              end={item.to === '/'}
              onClick={onNavigate}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-sm border-l-2 py-2 text-sm no-underline transition-colors duration-[180ms] ease-editorial',
                  collapsed ? 'justify-center' : 'px-3',
                  isActive
                    ? 'border-brand bg-brandTint/40 font-semibold text-brandDeep'
                    : 'border-transparent text-inkMid hover:bg-cream hover:text-ink',
                )
              }
            >
              <item.icon aria-hidden="true" className="size-4 shrink-0" />
              <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
